import { Banknote, CreditCard, ShieldCheck, AlertTriangle, RefreshCcw } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import type { PaymentStatus, PaymentType } from '../types';

interface Props {
  type: PaymentType | undefined;
  status?: PaymentStatus;
  className?: string;
}

/**
 * Compact payment indicator. Shows the payment method (cash vs online) plus a
 * tiny state hint for card payments (hold / captured / failed / refunded) so
 * the owner knows at a glance whether the money is secure before they commit
 * kitchen time.
 */
export function PaymentTypeChip({ type, status, className }: Props) {
  const isOnline = type === 'ONLINE';
  const Icon = isOnline ? CreditCard : Banknote;
  const base = isOnline ? 'Online' : 'Cash';

  const hint = isOnline ? onlineHint(status) : null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground',
        className
      )}
    >
      <Icon className="w-3 h-3" aria-hidden />
      <span>{base}</span>
      {hint && (
        <span
          className={cn(
            'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[10px]',
            hint.className
          )}
        >
          {hint.Icon && <hint.Icon className="w-2.5 h-2.5" aria-hidden />}
          {hint.label}
        </span>
      )}
    </span>
  );
}

function onlineHint(status: PaymentStatus | undefined) {
  switch (status) {
    case 'PENDING':
    case 'PROCESSING':
      return {
        label: 'Card held',
        className: 'bg-muted text-muted-foreground',
        Icon: ShieldCheck,
      };
    case 'SUCCEEDED':
      return {
        label: 'Paid',
        className: 'bg-success/10 text-success',
        Icon: ShieldCheck,
      };
    case 'FAILED':
    case 'CANCELLED':
      return {
        label: 'Failed',
        className: 'bg-destructive/10 text-destructive',
        Icon: AlertTriangle,
      };
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return {
        label: status === 'PARTIALLY_REFUNDED' ? 'Partial refund' : 'Refunded',
        className: 'bg-amber-500/10 text-amber-600',
        Icon: RefreshCcw,
      };
    default:
      return null;
  }
}
